import { StringContext } from "../../../core/StringContext";
import { StringDevModule } from "../core/StringDevModule";
import { StringObject } from "../../../objects/StringObject";
import { StringDevtoolSubBadge } from "../../../models/devtools/StringDevtool";
import { GridAdapter } from "./adapters/GridAdapter";
import { GridManager } from "./GridManager";
import { GridOverlay } from "./GridOverlay";
import { GridHUD } from "./GridHUD";
import { GridSerializer } from "./GridSerializer";

import { ColumnsAdapter } from "./adapters/ColumnsAdapter";
import { RowsAdapter } from "./adapters/RowsAdapter";
import { CenterAdapter } from "./adapters/CenterAdapter";
import { RuleOfThirdsAdapter } from "./adapters/RuleOfThirdsAdapter";
import { GoldenRectangleAdapter } from "./adapters/GoldenRectangleAdapter";
import { DotGridAdapter } from "./adapters/DotGridAdapter";
import { overlaySlotRegistry } from "../core/StringDevtoolsOverlayLayout";
import { StringDevViewportLayer } from "../core/StringDevViewportLayer";
import { buildGridCSS } from "./StringGrid.css";

const GRID_STORAGE_KEY_ATTR = "data-string-grid-storage-key";
const AUTO_STRING_OBJECT_ID_RE = /^string-\d+$/;
const GLOBAL_GRID_ELEMENT_ID = "__global__";

/**
 * Resolves a stable storage key for an element. Runtime-generated StringObject
 * ids are intentionally avoided because they do not reliably survive reloads.
 */
function resolveElementId(element: HTMLElement, object: StringObject): string {
  const existing = element.getAttribute(GRID_STORAGE_KEY_ATTR)?.trim();
  if (existing) {
    return existing;
  }

  const declaredStringId =
    element.getAttribute("data-string-id")?.trim() ||
    element.getAttribute("string-id")?.trim() ||
    "";

  if (declaredStringId && !AUTO_STRING_OBJECT_ID_RE.test(declaredStringId)) {
    const key = `string:${window.location.pathname}#${declaredStringId}`;
    element.setAttribute(GRID_STORAGE_KEY_ATTR, key);
    return key;
  }

  const domId = element.id.trim();
  if (domId) {
    const key = `dom:${window.location.pathname}#${domId}`;
    element.setAttribute(GRID_STORAGE_KEY_ATTR, key);
    return key;
  }

  const key = `path:${window.location.pathname}:${buildElementPathKey(element)}`;
  element.setAttribute(GRID_STORAGE_KEY_ATTR, key);
  return key;
}

function resolveLegacyElementIds(
  element: HTMLElement,
  object: StringObject,
  primaryId: string,
): string[] {
  const pathname = window.location.pathname;
  const domId = element.id.trim();
  const declaredStringId =
    element.getAttribute("data-string-id")?.trim() ||
    element.getAttribute("string-id")?.trim() ||
    "";

  const candidates = [
    domId ? `dom:${pathname}#${domId}` : "",
    declaredStringId && !AUTO_STRING_OBJECT_ID_RE.test(declaredStringId)
      ? `string:${pathname}#${declaredStringId}`
      : "",
    domId,
    declaredStringId,
    object.id.trim(),
  ].filter(Boolean);

  return candidates.filter(
    (candidate, index) => candidate !== primaryId && candidates.indexOf(candidate) === index,
  );
}

function buildElementPathKey(element: HTMLElement): string {
  const segments: string[] = [];
  let current: HTMLElement | null = element;

  while (current && current !== document.body && current !== document.documentElement) {
    const parent: HTMLElement | null = current.parentElement;
    const tag = current.tagName.toLowerCase();
    let index = 1;

    if (parent) {
      let sibling = current.previousElementSibling;
      while (sibling) {
        if (sibling.tagName === current.tagName) {
          index += 1;
        }
        sibling = sibling.previousElementSibling;
      }
    }

    segments.unshift(`${tag}:nth-of-type(${index})`);

    if (parent?.id) {
      segments.unshift(`#${parent.id}`);
      break;
    }

    current = parent;
  }

  return segments.join(">");
}

function resolveGridDepth(element: HTMLElement): number {
  let depth = 0;
  let current = element.parentElement;

  while (current) {
    if (current.hasAttribute("data-string-grid-root")) {
      depth += 1;
    }
    current = current.parentElement;
  }

  return depth;
}

interface GridTriggerEntry {
  object: StringObject;
  hud: GridHUD;
  depth: number;
}

interface GridTriggerMeasurement {
  visible: boolean;
  docX: number;
  docY: number;
  stackOffsetY: number;
  elementDocX?: number;
  elementDocY?: number;
  width?: number;
  height?: number;
}

/**
 * StringDevLayout — developer utility module for layout overlays.
 *
 * Usage:
 *   <div string="layout">...</div>
 *
 * No configuration attributes needed. All configuration happens
 * through the interactive HUD panel (hover top-right corner).
 *
 * Supports multiple grid types via the adapter pattern:
 * - Columns, Rows, Center
 * - Rule of Thirds, Phi Grid, Golden Rectangle, Harmonic Armature
 * - Extensible via `st.use(StringDevLayout, { adapters: [MyAdapter] })`
 */
export class StringDevLayout extends StringDevModule {
  private gridManager: GridManager = new GridManager();
  private viewportLayer: StringDevViewportLayer;
  private overlayLayer: StringDevViewportLayer;
  private overlays: Map<string, GridOverlay> = new Map();
  private huds: Map<string, GridHUD> = new Map();
  private elementMap: Map<string, HTMLElement> = new Map();
  private triggerEntries: Map<string, GridTriggerEntry> = new Map();
  private triggerMeasurements: Map<string, GridTriggerMeasurement> = new Map();
  private enabled: boolean = true;
  private needsMeasure = false;
  private hasPendingMutate = false;
  private _triggerSyncRafId: number | null = null;

  private globalHost: HTMLDivElement | null = null;
  private globalOverlay: GridOverlay | null = null;
  private globalHUD: GridHUD | null = null;
  private globalSubBadge: HTMLElement | null = null;
  static override devtool = {
    id: "layout",
    label: "Layout",
    icon: "layout",
    order: 20,
    group: 1,
    hotkey: { key: "L", shiftKey: true },
    styles: buildGridCSS,
    connects: { global: false, keys: ["layout"] },
  };

  constructor(context: StringContext) {
    super(context);
    this.attributesToMap = [];
    this.overlayLayer = this.acquireViewportLayer("devtools-grid-overlay", 10010);
    this.viewportLayer = this.acquireViewportLayer("devtools-hud", 10040);
    this.registerBuiltInAdapters();
    this.registerExternalAdapters();
  }

  // ── Lifecycle ─────────────────────────────────────────

  onInit(): void {
    this.ensureTriggerLayer();
    this.ensureGlobalGrid();
  }

  protected override getDevtoolSubBadges(): StringDevtoolSubBadge[] {
    return [
      {
        id: "global-settings",
        icon: "settings",
        label: "Open global grid settings",
        selectorAttribute: "data-string-grid-global-toggle",
        onClick: (anchorElement) => this.toggleGlobalPanel(anchorElement),
      },
    ];
  }

  onObjectConnected(object: StringObject): void {
    const element = object.htmlElement;
    const elementId = resolveElementId(element, object);
    const fallbackIds = resolveLegacyElementIds(element, object, elementId);
    element.setAttribute("data-string-grid-root", "");
    const gridDepth = resolveGridDepth(element);
    element.setAttribute("data-string-grid-depth", String(gridDepth));
    element.style.setProperty("--string-grid-stack-offset", "0px");
    this.elementMap.set(elementId, element);

    this.gridManager.load(elementId, fallbackIds);

    const overlayWorld = this.overlayLayer.getWorldElement(this.getWorldHost());
    const overlay = new GridOverlay(element, overlayWorld, () => {
      if (!this.enabled) return;
      this.renderElement(elementId);
      this.scheduleTriggerSync();
    });
    this.overlays.set(elementId, overlay);

    const hud = new GridHUD(element, this.gridManager.getAdapterRegistry(), {
      onAdd: (type) => this.handleAdd(elementId, type),
      onRemove: (id) => this.handleRemove(elementId, id),
      onToggle: (id) => this.handleToggle(elementId, id),
      onSettingChange: (id, key, value) => this.handleSettingChange(elementId, id, key, value),
      onReorder: (draggedId, targetId, placement) =>
        this.handleReorder(elementId, draggedId, targetId, placement),
      onMoveToEnd: (id) => this.handleMoveToEnd(elementId, id),
      onSelectLayout: (layoutId) => this.handleSelectLayout(elementId, layoutId),
      onUpdateLayoutMinWidth: (layoutId, minWidth) =>
        this.handleUpdateLayoutMinWidth(elementId, layoutId, minWidth),
      onAddLayout: () => this.handleAddLayout(elementId),
      onRemoveLayout: (layoutId) => this.handleRemoveLayout(elementId, layoutId),
      onExport: () => this.handleExport(elementId),
      onImport: () => this.handleImport(elementId),
      onRenameInstance: (instanceId, name) =>
        this.handleRenameInstance(elementId, instanceId, name),
      onRenamePanelTitle: (title) => this.handleRenamePanelTitle(elementId, title),
      onHUDOpen: () => this.handleHUDOpen(elementId),
      onHUDClose: () => this.handleHUDClose(elementId),
      onLayoutPanelOpen: () => this.handleLayoutPanelOpen(elementId),
      onLayoutPanelClose: () => this.handleLayoutPanelClose(elementId),
    });
    this.huds.set(elementId, hud);
    hud.setTargetId(elementId);
    this.ensureTriggerLayer();
    this.viewportLayer.getWorldElement(this.getWorldHost()).appendChild(hud.getHudElement());
    this.triggerEntries.set(elementId, { object, hud, depth: gridDepth });

    this.renderElement(elementId);
    this.refreshHUD(elementId);
    overlay.setEnabled(this.enabled);
    hud.setEnabled(this.enabled);
    requestAnimationFrame(() => this.scheduleTriggerSync());
  }

  onObjectDisconnected(object: StringObject): void {
    const element = object.htmlElement;
    const elementId = resolveElementId(element, object);
    this.destroyElement(elementId);
  }

  onResize(): void {
    for (const [elementId, overlay] of this.overlays) {
      overlay.syncViewBox();
      this.renderElement(elementId);
      this.refreshHUD(elementId);
    }
    this.syncGlobalHostSize();
    this.globalOverlay?.syncViewBox();
    this.renderGlobal();
    this.refreshGlobalHUD();
    this.scheduleTriggerSync();
  }

  onScroll(): void {
    if (!this.enabled) return;
    this.needsMeasure = true;
  }

  onScrollMeasure(): void {
    if (!this.enabled || !this.needsMeasure) return;
    this.collectTriggerMeasurements();
    this.needsMeasure = false;
    for (const [elementId, entry] of this.triggerEntries) {
      this.applyTriggerMeasurement(entry, this.triggerMeasurements.get(elementId));
    }
    this.hasPendingMutate = false;
  }

  onMutate(): void {
    if (!this.enabled || !this.hasPendingMutate) return;
    for (const [elementId, entry] of this.triggerEntries) {
      this.applyTriggerMeasurement(entry, this.triggerMeasurements.get(elementId));
    }
    this.hasPendingMutate = false;
  }

  onDOMRebuild(): void {
    if (!this.enabled) return;
    this.scheduleTriggerSync();
  }

  destroy(): void {
    for (const [id] of this.overlays) {
      this.destroyElement(id);
    }
    this.destroyGlobalGrid();
    this.gridManager.clear();
    super.destroy();
  }

  public setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) {
      return;
    }

    this.enabled = enabled;
    for (const overlay of this.overlays.values()) {
      overlay.setEnabled(enabled);
    }
    for (const hud of this.huds.values()) {
      hud.setEnabled(enabled);
    }
    this.globalOverlay?.setEnabled(enabled);
    this.globalHUD?.setEnabled(enabled);
    if (!enabled) {
      this.globalHUD?.hide();
    }
    this.applyTriggerLayerState();
    if (enabled) {
      for (const elementId of this.overlays.keys()) {
        this.renderElement(elementId);
      }
      this.renderGlobal();
      this.scheduleTriggerSync();
    } else {
      this.hasPendingMutate = false;
    }
    this.emitDevtoolState();
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  protected getDevtoolActiveState(): boolean {
    return this.enabled;
  }

  protected setDevtoolActiveState(active: boolean): void {
    this.setEnabled(active);
  }

  // ── HUD Callbacks ─────────────────────────────────────

  private handleAdd(elementId: string, type: string): void {
    this.gridManager.addInstance(elementId, type);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleRemove(elementId: string, instanceId: string): void {
    const instance = this.gridManager.findInstance(elementId, instanceId);
    if (instance) {
      const adapter = this.gridManager.getAdapter(instance.type);
      const overlay = this.overlays.get(elementId);
      if (adapter && overlay) {
        overlay.clearInstance(instanceId, adapter);
      }
    }
    this.gridManager.removeInstance(elementId, instanceId);
    this.refreshHUD(elementId);
  }

  private handleToggle(elementId: string, instanceId: string): void {
    this.gridManager.toggleVisibility(elementId, instanceId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleSettingChange(
    elementId: string,
    instanceId: string,
    key: string,
    value: any,
  ): void {
    this.gridManager.updateSetting(elementId, instanceId, key, value);
    this.renderElement(elementId);
  }

  private handleReorder(
    elementId: string,
    draggedId: string,
    targetId: string,
    placement: "before" | "after",
  ): void {
    this.gridManager.reorderInstance(elementId, draggedId, targetId, placement);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleMoveToEnd(elementId: string, instanceId: string): void {
    this.gridManager.moveInstanceToEnd(elementId, instanceId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleSelectLayout(elementId: string, layoutId: string): void {
    this.gridManager.selectLayout(elementId, layoutId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleUpdateLayoutMinWidth(elementId: string, layoutId: string, minWidth: number): void {
    this.gridManager.updateLayoutMinWidth(elementId, layoutId, minWidth);
    this.syncSelectedLayoutToViewport(elementId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleAddLayout(elementId: string): void {
    this.gridManager.addLayout(elementId);
    this.syncSelectedLayoutToViewport(elementId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleRemoveLayout(elementId: string, layoutId: string): void {
    this.gridManager.removeLayout(elementId, layoutId);
    this.syncSelectedLayoutToViewport(elementId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleRenameInstance(elementId: string, instanceId: string, name: string): void {
    this.gridManager.renameInstance(elementId, instanceId, name);
    this.refreshHUD(elementId);
  }

  private handleRenamePanelTitle(elementId: string, title: string): void {
    this.gridManager.renamePanelTitle(elementId, title);
    this.refreshHUD(elementId);
  }

  private handleHUDOpen(elementId: string): void {
    this.syncToResolved(elementId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleHUDClose(elementId: string): void {
    this.syncToResolved(elementId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleLayoutPanelOpen(elementId: string): void {
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleLayoutPanelClose(elementId: string): void {
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleExport(elementId: string): void {
    const layouts = this.gridManager.getLayouts(elementId);
    const selectedLayoutId = this.gridManager.getSelectedLayoutId(elementId);
    const data = GridSerializer.serialize(layouts, selectedLayoutId);
    const slug = elementId.replace(/[^a-z0-9]/gi, "-").toLowerCase() || "element";
    GridSerializer.toFile(`string-grid-${slug}.json`, data);
  }

  private handleImport(elementId: string): void {
    GridSerializer.fromFile()
      .then((raw) => {
        const result = GridSerializer.deserialize(raw, this.gridManager.getAdapterRegistry());
        if (!result) return;
        this.gridManager.replaceLayouts(elementId, result.layouts, result.selectedLayoutId);
        this.syncSelectedLayoutToViewport(elementId);
        this.renderElement(elementId);
        this.refreshHUD(elementId);
      })
      .catch(() => {
        // user cancelled or invalid file — nothing to do
      });
  }

  // ── Internal ──────────────────────────────────────────

  private renderElement(elementId: string): void {
    if (elementId === GLOBAL_GRID_ELEMENT_ID) {
      this.renderGlobal();
      return;
    }

    const overlay = this.overlays.get(elementId);
    if (!overlay) return;

    const hud = this.huds.get(elementId);
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const instances = hud?.isHUDOpen()
      ? this.gridManager.getSelectedInstances(elementId)
      : this.gridManager.getResolvedInstances(elementId, viewportWidth);
    overlay.render(instances, this.gridManager.getAdapterRegistry());
  }

  private syncToResolved(elementId: string): void {
    this.syncSelectedLayoutToViewport(elementId);
  }

  private syncSelectedLayoutToViewport(elementId: string): void {
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const resolvedLayoutId = this.gridManager.getResolvedLayoutId(elementId, viewportWidth);
    const selectedLayoutId = this.gridManager.getSelectedLayoutId(elementId);
    if (selectedLayoutId !== resolvedLayoutId) {
      this.gridManager.selectLayout(elementId, resolvedLayoutId, false);
    }
  }

  private refreshHUD(elementId: string): void {
    if (elementId === GLOBAL_GRID_ELEMENT_ID) {
      this.refreshGlobalHUD();
      return;
    }

    const hud = this.huds.get(elementId);
    if (!hud) return;

    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const resolvedLayoutId = this.gridManager.getResolvedLayoutId(elementId, viewportWidth);
    if (!hud.isHUDOpen()) {
      const selectedLayoutId = this.gridManager.getSelectedLayoutId(elementId);
      if (selectedLayoutId !== resolvedLayoutId) {
        this.gridManager.selectLayout(elementId, resolvedLayoutId, false);
      }
    }
    const selectedLayoutId = this.gridManager.getSelectedLayoutId(elementId);
    const instances = this.gridManager.getSelectedInstances(elementId);
    const layouts = this.gridManager.getLayouts(elementId);
    const panelTitle = this.gridManager.getPanelTitle(elementId);
    hud.refresh(instances, layouts, panelTitle, selectedLayoutId, resolvedLayoutId);
  }

  // ── Global grid ───────────────────────────────────────

  private ensureGlobalGrid(): void {
    if (this.globalOverlay && this.globalHUD) return;

    const screen = this.overlayLayer.getScreenElement();

    const host = document.createElement("div");
    host.setAttribute("data-string-grid-global-root", "");
    host.style.position = "absolute";
    host.style.inset = "0";
    host.style.pointerEvents = "none";
    screen.appendChild(host);
    this.globalHost = host;
    this.syncGlobalHostSize();

    this.gridManager.load(GLOBAL_GRID_ELEMENT_ID);

    const overlay = new GridOverlay(host, screen, () => {
      if (!this.enabled) return;
      this.renderGlobal();
    });
    this.globalOverlay = overlay;
    overlay.setEnabled(this.enabled);
    overlay.setPosition(0, 0, host.clientWidth, host.clientHeight);

    const hud = new GridHUD(
      host,
      this.gridManager.getAdapterRegistry(),
      {
        onAdd: (type) => this.handleAdd(GLOBAL_GRID_ELEMENT_ID, type),
        onRemove: (id) => this.handleRemove(GLOBAL_GRID_ELEMENT_ID, id),
        onToggle: (id) => this.handleToggle(GLOBAL_GRID_ELEMENT_ID, id),
        onSettingChange: (id, key, value) =>
          this.handleSettingChange(GLOBAL_GRID_ELEMENT_ID, id, key, value),
        onReorder: (draggedId, targetId, placement) =>
          this.handleReorder(GLOBAL_GRID_ELEMENT_ID, draggedId, targetId, placement),
        onMoveToEnd: (id) => this.handleMoveToEnd(GLOBAL_GRID_ELEMENT_ID, id),
        onSelectLayout: (layoutId) => this.handleSelectLayout(GLOBAL_GRID_ELEMENT_ID, layoutId),
        onUpdateLayoutMinWidth: (layoutId, minWidth) =>
          this.handleUpdateLayoutMinWidth(GLOBAL_GRID_ELEMENT_ID, layoutId, minWidth),
        onAddLayout: () => this.handleAddLayout(GLOBAL_GRID_ELEMENT_ID),
        onRemoveLayout: (layoutId) => this.handleRemoveLayout(GLOBAL_GRID_ELEMENT_ID, layoutId),
        onExport: () => this.handleExport(GLOBAL_GRID_ELEMENT_ID),
        onImport: () => this.handleImport(GLOBAL_GRID_ELEMENT_ID),
        onRenameInstance: (instanceId, name) =>
          this.handleRenameInstance(GLOBAL_GRID_ELEMENT_ID, instanceId, name),
        onRenamePanelTitle: (title) => this.handleRenamePanelTitle(GLOBAL_GRID_ELEMENT_ID, title),
        onHUDOpen: () => {
          this.syncToResolved(GLOBAL_GRID_ELEMENT_ID);
          this.renderGlobal();
          this.refreshGlobalHUD();
          this.syncGlobalSubBadgeState();
        },
        onHUDClose: () => {
          this.syncToResolved(GLOBAL_GRID_ELEMENT_ID);
          this.renderGlobal();
          this.refreshGlobalHUD();
          this.syncGlobalSubBadgeState();
        },
        onLayoutPanelOpen: () => this.refreshGlobalHUD(),
        onLayoutPanelClose: () => this.refreshGlobalHUD(),
      },
      { attachTrigger: false },
    );
    this.globalHUD = hud;
    hud.setTargetId(GLOBAL_GRID_ELEMENT_ID);
    hud.setEnabled(this.enabled);

    const hudScreen = this.viewportLayer.getScreenElement();
    const hudRoot = hud.getHudElement();
    hudRoot.setAttribute("data-string-grid-global-hud", "");
    hudRoot.style.position = "absolute";
    hudRoot.style.left = "24px";
    hudRoot.style.bottom = "96px";
    hudRoot.style.pointerEvents = "auto";
    hudScreen.appendChild(hudRoot);
    hud.setAnchorVisible(true);

    this.renderGlobal();
    this.refreshGlobalHUD();
  }

  private syncGlobalHostSize(): void {
    const host = this.globalHost;
    if (!host) return;
    const width = this.data.viewport.windowWidth || window.innerWidth;
    const height = this.data.viewport.windowHeight || window.innerHeight;
    host.style.width = `${width}px`;
    host.style.height = `${height}px`;
    this.globalOverlay?.setPosition(0, 0, width, height);
  }

  private renderGlobal(): void {
    const overlay = this.globalOverlay;
    if (!overlay) return;
    const hud = this.globalHUD;
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const instances = hud?.isHUDOpen()
      ? this.gridManager.getSelectedInstances(GLOBAL_GRID_ELEMENT_ID)
      : this.gridManager.getResolvedInstances(GLOBAL_GRID_ELEMENT_ID, viewportWidth);
    overlay.render(instances, this.gridManager.getAdapterRegistry());
  }

  private refreshGlobalHUD(): void {
    const hud = this.globalHUD;
    if (!hud) return;
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const resolvedLayoutId = this.gridManager.getResolvedLayoutId(
      GLOBAL_GRID_ELEMENT_ID,
      viewportWidth,
    );
    if (!hud.isHUDOpen()) {
      const selectedLayoutId = this.gridManager.getSelectedLayoutId(GLOBAL_GRID_ELEMENT_ID);
      if (selectedLayoutId !== resolvedLayoutId) {
        this.gridManager.selectLayout(GLOBAL_GRID_ELEMENT_ID, resolvedLayoutId, false);
      }
    }
    const selectedLayoutId = this.gridManager.getSelectedLayoutId(GLOBAL_GRID_ELEMENT_ID);
    const instances = this.gridManager.getSelectedInstances(GLOBAL_GRID_ELEMENT_ID);
    const layouts = this.gridManager.getLayouts(GLOBAL_GRID_ELEMENT_ID);
    const panelTitle = this.gridManager.getPanelTitle(GLOBAL_GRID_ELEMENT_ID);
    hud.refresh(instances, layouts, panelTitle, selectedLayoutId, resolvedLayoutId);
  }

  private toggleGlobalPanel(anchorElement?: HTMLElement): void {
    this.ensureGlobalGrid();
    if (anchorElement) {
      this.globalSubBadge = anchorElement;
    }
    this.positionGlobalHudAtAnchor(anchorElement);
    this.globalHUD?.toggle();
    this.syncGlobalSubBadgeState();
  }

  private syncGlobalSubBadgeState(): void {
    if (!this.globalSubBadge) return;
    const open = this.globalHUD?.isHUDOpen() ?? false;
    this.globalSubBadge.setAttribute("data-active", open ? "true" : "false");
    this.globalSubBadge.setAttribute("aria-expanded", open ? "true" : "false");
  }

  private positionGlobalHudAtAnchor(anchorElement?: HTMLElement): void {
    if (!anchorElement || !this.globalHUD) {
      return;
    }

    const hudRoot = this.globalHUD.getHudElement();
    const screen = this.viewportLayer.getScreenElement();
    const anchorRect = anchorElement.getBoundingClientRect();
    const screenRect = screen.getBoundingClientRect();
    hudRoot.style.left = `${Math.round(anchorRect.right - screenRect.left)}px`;
    hudRoot.style.top = `${Math.round(anchorRect.top - screenRect.top + 28)}px`;
    hudRoot.style.bottom = "auto";
    hudRoot.style.transform = "";
  }

  private destroyGlobalGrid(): void {
    this.globalOverlay?.destroy();
    this.globalOverlay = null;
    this.globalHUD?.destroy();
    this.globalHUD = null;
    this.globalHost?.remove();
    this.globalHost = null;
  }

  private destroyElement(elementId: string): void {
    this.overlays.get(elementId)?.destroy();
    this.overlays.delete(elementId);
    this.huds.get(elementId)?.destroy();
    this.huds.delete(elementId);
    this.triggerEntries.delete(elementId);
    this.triggerMeasurements.delete(elementId);
    this.elementMap.get(elementId)?.removeAttribute("data-string-grid-root");
    this.elementMap.get(elementId)?.removeAttribute("data-string-grid-depth");
    this.elementMap.get(elementId)?.removeAttribute(GRID_STORAGE_KEY_ATTR);
    this.elementMap.get(elementId)?.style.removeProperty("--string-grid-stack-offset");
    this.elementMap.delete(elementId);
  }

  private ensureTriggerLayer(): void {
    const layer = this.viewportLayer.ensure(this.getWorldHost());
    layer.setAttribute("data-string-grid-trigger-layer", "");
  }

  private applyTriggerLayerState(): void {
    return;
  }

  private scheduleTriggerSync(): void {
    if (this._triggerSyncRafId !== null) return;
    this._triggerSyncRafId = requestAnimationFrame(() => {
      this._triggerSyncRafId = null;
      if (!this.enabled) return;
      this.ensureTriggerLayer();
      this.needsMeasure = true;
      this.collectTriggerMeasurements();
      this.needsMeasure = false;
      for (const [elementId, entry] of this.triggerEntries) {
        this.applyTriggerMeasurement(entry, this.triggerMeasurements.get(elementId));
      }
      this.hasPendingMutate = false;
    });
  }

  private collectTriggerMeasurements(): void {
    for (const [elementId, entry] of this.triggerEntries) {
      this.triggerMeasurements.set(elementId, this.measureTrigger(entry));
    }
  }

  private measureTrigger(entry: GridTriggerEntry): GridTriggerMeasurement {
    const object = entry.object;
    const element = object.htmlElement;
    if (!this.enabled || !element || !element.isConnected) {
      return { visible: false, docX: 0, docY: 0, stackOffsetY: 0 };
    }

    const scrollLeft = this.getViewportScrollLeft();
    const scrollTop = this.getViewportScrollTop();
    const rect = element.getBoundingClientRect();
    const width = rect.width || element.offsetWidth || element.clientWidth || 0;
    const height = rect.height || element.offsetHeight || element.clientHeight || 0;
    const contentX = rect.left + scrollLeft;
    const contentY = rect.top + scrollTop;

    if (width <= 0 || height <= 0) {
      return { visible: false, docX: 0, docY: 0, stackOffsetY: 0 };
    }

    const viewportHeight = this.data.viewport.windowHeight || window.innerHeight;
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const viewportLeft = rect.left;
    const viewportTop = rect.top;
    const isOffscreen =
      viewportTop + height < 0 ||
      viewportTop > viewportHeight ||
      viewportLeft + width < 0 ||
      viewportLeft > viewportWidth;

    if (isOffscreen) {
      return { visible: false, docX: 0, docY: 0, stackOffsetY: 0 };
    }

    const triggerDocX = contentX + width;
    const baseTriggerDocY = contentY;
    const stackOffsetY = this.resolveTriggerStackOffset(entry, triggerDocX, baseTriggerDocY);

    return {
      visible: true,
      docX: triggerDocX,
      docY: baseTriggerDocY + stackOffsetY,
      stackOffsetY,
      elementDocX: contentX,
      elementDocY: contentY,
      width,
      height,
    };
  }

  private applyTriggerMeasurement(
    entry: GridTriggerEntry,
    measurement: GridTriggerMeasurement | undefined,
  ): void {
    if (!measurement || !measurement.visible) {
      entry.hud.setAnchorVisible(false);
      entry.object.htmlElement.style.setProperty("--string-grid-stack-offset", "0px");
      return;
    }

    entry.hud.setAnchorVisible(true);
    entry.object.htmlElement.style.setProperty(
      "--string-grid-stack-offset",
      `${measurement.stackOffsetY}px`,
    );
    entry.hud.setAnchorPosition(measurement.docX, measurement.docY, "top-right");

    const overlay = this.overlays.get(resolveElementId(entry.object.htmlElement, entry.object));
    if (
      overlay &&
      measurement.elementDocX != null &&
      measurement.elementDocY != null &&
      measurement.width != null &&
      measurement.height != null
    ) {
      overlay.setPosition(
        measurement.elementDocX,
        measurement.elementDocY,
        measurement.width,
        measurement.height,
      );
    }
  }

  protected getWorldHost(): HTMLElement {
    return this.data.scroll.container ?? document.body ?? document.documentElement;
  }

  private getObjectDocY(object: StringObject): number {
    const start = object.getProperty<number>("start");
    const size = object.getProperty<number>("size");
    if (start != null && size != null && size > 0) {
      return start;
    }
    return object.getProperty<number>("layout-doc-top") ?? 0;
  }

  private getViewportScrollLeft(): number {
    const scroller = this.data.scroll.elementContainer;
    return scroller?.scrollLeft ?? 0;
  }

  private getViewportScrollTop(): number {
    const scroller = this.data.scroll.elementContainer;
    return scroller?.scrollTop ?? 0;
  }

  private resolveTriggerStackOffset(
    entry: GridTriggerEntry,
    triggerDocX: number,
    triggerDocY: number,
  ): number {
    return overlaySlotRegistry.resolveCollisionOffset(
      entry.object.htmlElement,
      triggerDocX,
      triggerDocY,
      (ancestorId) => {
        const ancestorObject = this.objectManager.all.get(ancestorId);
        if (!ancestorObject) {
          return null;
        }

        const ancestorWidth =
          ancestorObject.getProperty<number>("layout-width") ??
          ancestorObject.htmlElement.offsetWidth ??
          ancestorObject.htmlElement.clientWidth ??
          0;

        return {
          docX:
            (ancestorObject.getProperty<number>("layout-doc-left") ?? 0) + ancestorWidth,
          docY: this.getObjectDocY(ancestorObject),
        };
      },
    );
  }

  private registerBuiltInAdapters(): void {
    const builtIn: GridAdapter[] = [
      new ColumnsAdapter(),
      new RowsAdapter(),
      new CenterAdapter(),
      new GoldenRectangleAdapter(),
      new RuleOfThirdsAdapter(),
      new DotGridAdapter(),
    ];

    for (const adapter of builtIn) {
      this.gridManager.registerAdapter(adapter);
    }
  }

  private registerExternalAdapters(): void {
    const external = this.settings["adapters"] as GridAdapter[] | undefined;
    if (!external) return;

    for (const adapter of external) {
      this.gridManager.registerAdapter(adapter);
    }
  }
}

export { StringDevLayout as StringGrid };
